import { Box, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useState } from 'react';
import { object, string } from 'yup';
import {
  ResetOwnAdminMfaDocument,
  ResetOwnAdminMfaMutation,
  ResetOwnAdminMfaMutationVariables,
  VerifyAdminMfaBackupCodeDocument,
  VerifyAdminMfaBackupCodeMutation,
  VerifyAdminMfaBackupCodeMutationVariables,
  VerifyAdminMfaDocument,
  VerifyAdminMfaMutation,
  VerifyAdminMfaMutationVariables,
} from '../../../generated/graphql';
import useSilentMutation from '../../../graphql/hooks/useSilentMutation';
import { useAuth } from '../../../providers/auth.provider';
import { PasswordField, TextField } from '../../../ui';

type ChallengeMode = 'totp' | 'backup' | 'reset';

const totpValidation = object({
  code: string()
    .required('Enter the 6-digit code')
    .matches(/^\d{6}$/, 'Enter the 6-digit code'),
});

const backupValidation = object({
  code: string().required('Enter a backup code'),
});

const CHALLENGE_COPY: Record<ChallengeMode, { title: string; helper: string }> =
  {
    totp: {
      title: 'Authenticator code',
      helper: 'Enter the current 6-digit code from your authenticator app.',
    },
    backup: {
      title: 'Backup code',
      helper: 'A backup code signs this session in and cannot be used again.',
    },
    reset: {
      title: 'Reset authenticator',
      helper:
        'This wipes the current authenticator and remaining backup codes. You will enroll a new one next.',
    },
  };

export const MfaChallenge = () => {
  const { completeMfa } = useAuth();
  const [mode, setMode] = useState<ChallengeMode>('totp');
  const [verifyTotp, { loading: totpLoading }] = useSilentMutation<
    VerifyAdminMfaMutation,
    VerifyAdminMfaMutationVariables
  >(VerifyAdminMfaDocument);
  const [verifyBackup, { loading: backupLoading }] = useSilentMutation<
    VerifyAdminMfaBackupCodeMutation,
    VerifyAdminMfaBackupCodeMutationVariables
  >(VerifyAdminMfaBackupCodeDocument);
  const [resetOwn, { loading: resetLoading }] = useSilentMutation<
    ResetOwnAdminMfaMutation,
    ResetOwnAdminMfaMutationVariables
  >(ResetOwnAdminMfaDocument);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { code: '' },
    validationSchema: mode === 'totp' ? totpValidation : backupValidation,
    onSubmit: async ({ code }) => {
      if (mode === 'totp') {
        await verifyTotp({ variables: { code } });
        await completeMfa();
        return;
      }
      if (mode === 'backup') {
        await verifyBackup({ variables: { code } });
        await completeMfa();
        return;
      }
      await resetOwn({ variables: { backupCode: code } });
      window.location.reload();
    },
  });

  const loading = totpLoading || backupLoading || resetLoading;
  const { title, helper } = CHALLENGE_COPY[mode];

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      display="grid"
      rowGap={2.5}
    >
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body2">{helper}</Typography>
      {mode === 'totp' ? (
        <TextField
          id="code"
          name="code"
          label="Authentication code"
          value={formik.values.code}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
          sx={{ borderRadius: '8px' }}
        />
      ) : (
        <PasswordField
          id="code"
          name="code"
          label="Backup code"
          value={formik.values.code}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
          sx={{ borderRadius: '8px' }}
        />
      )}
      <Button
        variant="contained"
        disabled={formik.isSubmitting || loading}
        type="submit"
        sx={{ minWidth: 160, justifySelf: 'end' }}
      >
        Continue
      </Button>
      <Box display="grid" justifyItems="start">
        {mode !== 'totp' ? (
          <Button
            type="button"
            onClick={() => {
              setMode('totp');
              formik.resetForm();
            }}
          >
            Use authenticator code
          </Button>
        ) : null}
        {mode !== 'backup' ? (
          <Button
            type="button"
            onClick={() => {
              setMode('backup');
              formik.resetForm();
            }}
          >
            Use a backup code
          </Button>
        ) : null}
        {mode !== 'reset' ? (
          <Button
            type="button"
            onClick={() => {
              setMode('reset');
              formik.resetForm();
            }}
          >
            Lost authenticator? Reset with a backup code
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};
